import i18n from '@/core/i18n';
import {
  ChangedFieldsStrategy,
  GenericChangedField,
} from '@/modules/common/components/changed-fields';

import {
  CORPORATE_FUNCTION_CAPACITY_FIELD,
  TeamMemberFieldNameDTO,
} from '../../types/dto/team-member-changed-field.dto';
import { getLocalizedFieldName } from '../../utils/team-member-changes/team-member-change-field-name-mapper';
import {
  formatFieldValue,
  formatValueWithCurrency,
} from '../../utils/team-member-changes/team-member-change-field-value-formatter';

/**
 * Computes corporate function capacity (100% - billable capacity) as a string.
 * Returns null if the capacity value cannot be parsed.
 */
const computeCorporateFunctionValue = (capacityValue: string | null): string | null => {
  if (!capacityValue) return null;
  const num = Number(capacityValue);
  if (isNaN(num)) return null;
  return String(parseFloat((100 - num).toFixed(2)));
};

const isRateField = (field: string): boolean =>
  field === TeamMemberFieldNameDTO.INTERNAL_RATE || field === TeamMemberFieldNameDTO.EXTERNAL_RATE;

const resolveCurrency = (allFields: GenericChangedField[]): string | null => {
  const currencyField = allFields.find(f => f.field === TeamMemberFieldNameDTO.CURRENCY);
  return currencyField?.to || currencyField?.from || null;
};

/**
 * Builds the team-domain strategy for the generic ChangedFieldsCell.
 *
 * Carries team-only concerns: synthetic corporate-function-capacity rows,
 * INTERNAL/EXTERNAL_RATE currency, "No department/role/seniority" placeholders,
 * and the "member created" empty state for the initial change.
 *
 * Built lazily (at render) so i18n strings resolve after resources load.
 */
export const createTeamChangedFieldsStrategy = (): ChangedFieldsStrategy => {
  // Namespace-bound t that mirrors useTranslation('authenticated.team'): relative keys
  // resolve in-namespace, while explicit "ns:key" keys (e.g. common:yes) still work.
  const t = i18n.getFixedT(null, 'authenticated.team');

  // Injects a synthetic "Corporate function capacity" field after each CAPACITY field.
  const expandCapacityFields = (fields: GenericChangedField[]): GenericChangedField[] => {
    const corporateFunctionLabel = t('details.tabs.changes.fieldNames.corporateFunctionCapacity');
    const result: GenericChangedField[] = [];
    for (const field of fields) {
      result.push(field);
      if (field.field === TeamMemberFieldNameDTO.CAPACITY) {
        result.push({
          field: CORPORATE_FUNCTION_CAPACITY_FIELD,
          fieldLocalizedName: corporateFunctionLabel,
          from: computeCorporateFunctionValue(field.from),
          to: computeCorporateFunctionValue(field.to),
          subfieldChanges: null,
        });
      }
    }
    return result;
  };

  // Strikethrough placeholder shown when the old value is null for certain fields.
  const getEmptyOldValuePlaceholder = (field: string): string | null => {
    switch (field) {
      case TeamMemberFieldNameDTO.DEPARTMENT:
        return t('details.tabs.changes.valueDisplay.noDepartment');
      case TeamMemberFieldNameDTO.ROLE:
        return t('details.tabs.changes.valueDisplay.noRole');
      case TeamMemberFieldNameDTO.SENIORITY:
        return t('details.tabs.changes.valueDisplay.noSeniority');
      default:
        return null;
    }
  };

  return {
    getLocalizedFieldName: field => getLocalizedFieldName(field),

    formatFieldValue: (value, field) => formatFieldValue(value, t, field),

    renderValue: (formatted, field, allFields) => {
      if (isRateField(field)) {
        const currency = resolveCurrency(allFields);
        if (currency) return formatValueWithCurrency(formatted, currency);
      }
      return formatted;
    },

    getEmptyOldValuePlaceholder,

    preprocessFields: expandCapacityFields,

    getEmptyStateMessage: ({ isInitial }) =>
      isInitial ? t('details.tabs.changes.memberCreated') : t('details.tabs.changes.noChanges'),

    strings: {
      arrow: t('details.tabs.changes.valueDisplay.arrow'),
      changesCount: count => t('details.tabs.changes.changesCount', { count }),
    },
  };
};
